<script lang="ts">
  import {setAnimationControllerLoading} from '@/components/animations/AnimationController.svelte';
  import Icon from '@/components/icon/Icon.svelte';
  import editorInstance from '@/lib/editor/editor';
  import editorStore from '@/lib/editor/editor.store';
  import {ThumbTemplatesService} from '@/services';
  import {getInstance} from '@/services/animation';
  import {preview, thumbnail, thumbTemplates} from '@/store';
  import type {GridConfig, PreviewImage} from '@/types/common';
  import type {Shapes, Tags} from '@/types/filters';
  import type {Template} from '@/types/project';
  import {onDestroy, onMount} from 'svelte/internal';
  import Swal from 'sweetalert2';
  import tippy from 'tippy.js';

  export const title = 'Templates';

  const shapes = ['Square', 'Vertical', 'Horizontal'];

  const isAdmin = window.uData && window.uData.is_admin;

  let gridProps: GridConfig = {
    images: [],
    loading: true,
    columnCount: 4
  };
  let isOpen = false;
  // For Filtering
  let filterShape: Shapes = '';
  let filterTags: Tags[] = [];
  let filterFavorites: boolean = false;
  let activeFilter: Shapes = '';

  let page: number = 1;
  let limit: number = 16;
  let selectedTags: string = '';
  let selectedShape: Shapes = '';
  let favorites: boolean = false;
  let loadMore: Boolean = false;
  let fullSidebar: boolean = false;
  let fullHeight: boolean = false;

  let favoriteTooltip: HTMLElement;
  let unFavoriteTooltip: HTMLElement;

  const addGridProps = (filtered: Template[]) => {
    const images = filtered.map(template => {
      let obj: PreviewImage = {
        id: template.id,
        src: template.preview,
        title: template.title,
        content: template.content,
        isFeatured: template.isFeatured,
        isFavorite: template.isFavorite,
        isFree: template.isFree
      };

      return obj;
    });

    gridProps = {
      ...gridProps,
      loading: false,
      images: images
    };
  };

  const unsubscribeToTemplate = thumbTemplates.subscribe(t => {
    addGridProps(t.items);
  });

  onMount(async () => {
    await thumbTemplates.getTemplates(page, limit, selectedShape, selectedTags, favorites, false, $thumbnail.type);
  });

  onDestroy(() => {
    unsubscribeToTemplate();
  });

  let changeTemplateId = -1;

  const changeTemplate = async (image: PreviewImage) => {
    const template = (await thumbTemplates.getTemplate(image.id)) as Template;
    image.content = template?.content;
    changeTemplateId = changeTemplateId++;
    let currentTemplateId = changeTemplateId;

    console.log('Changing templates ...');
    setAnimationControllerLoading(true);
    //editorStore.setActiveComponent(null);
    const instance = getInstance();
    editorStore.setActiveComponent(null);
    // console.log(instance)
    // Stop/Pause playing before changing the template

    //previewAreaLoadingStore.set(true);
    thumbnail.selectTemplate(image as unknown as Template);
    await instance.buildTemplate(image.content, true);
    window.dispatchEvent(new Event('reset-timeline'));
    window.dispatchEvent(new Event('reset-download'));
    preview.set({
      checkIfSermonTitleExist: instance.checkForSermonTitle(),
      checkIfBrandLogoExists: instance.checkForBrandLogo()
    });

    // We need to give it some time to make sure that the template
    const tryToBuild: any = (retries = 0) =>
      setTimeout(async () => {
        if (changeTemplateId !== currentTemplateId) {
          return console.log('Not the same template id', {changeTemplateId, currentTemplateId});
        }

        changeTemplateId = -1;

        console.log('Reattaching video and initializing loaders...');

        instance.reattachVideo();
        instance.templateLoader.initializeLoaders();

        try {
          // Check to see if we can fetch dimensions
          const dimensions = instance.templateLoader.templateMetadata.captionDimensions;
        } catch (err) {
          console.warn('err', err);
          if (retries < 10) {
            return tryToBuild(retries + 1);
          } else {
            alert('There may have been an issue loading the template. Please try again.');
          }
        }
        instance.buildAnimations(true);
        instance.rebuildChanges();
        editorInstance.reinitialize();
      }, 500);

    if ($thumbnail.snapshot) editorInstance.insertPastorPic($thumbnail.snapshot);
    if ($thumbnail.quote) editorInstance.insertQuote($thumbnail.quote);
    if ($thumbnail.title) editorInstance.insertTitle($thumbnail.title);
    if ($thumbnail.pastorName) editorInstance.insertPastorName($thumbnail.pastorName);

    tryToBuild();
  };

  const appendTemplates = async () => {
    if (page != 1) {
      await thumbTemplates.getTemplates(page, limit, selectedShape, selectedTags, favorites, false, $thumbnail.type);
    }
  };

  const checkScrollEvent = (e: Event) => {
    const target = e.target as Element;
    const {scrollHeight, scrollTop, clientHeight} = target;

    loadMore = Math.abs(scrollHeight - scrollTop - 2) <= Math.abs(clientHeight);
  };

  const setActiveFilter = (shape: Shapes | string) => {
    activeFilter = shape as Shapes;

    if (filterShape === activeFilter) {
      filterShape = 'All';
    } else {
      filterShape = activeFilter;
    }
  };

  const filter = async (shape: Shapes, tags: Tags[], favorite: boolean) => {
    page = 1;
    let changed = false;

    if (favorite != favorites) {
      changed = true;
      favorites = favorite;
    }

    if (tags.length == 0 && selectedTags != '') {
      changed = true;
    }

    if (shape == 'All') {
      selectedShape = '';
    } else {
      selectedShape = shape;
    }

    if (tags.length >= 1) {
      selectedTags = tags.join(',');
    } else {
      selectedTags = '';
    }

    if (selectedTags.length != 0 || changed == true || selectedShape != '' || selectedTags != '' || shape == 'All') {
      gridProps.loading = true;
      thumbTemplates.set({items: []});

      await thumbTemplates.getTemplates(page, limit, selectedShape, selectedTags, favorites, true, $thumbnail.type);
    }
  };

  $: if (loadMore) {
    page = page + 1;
    appendTemplates();
  }

  $: filter(filterShape, filterTags, filterFavorites);

  $: {
    if (favoriteTooltip) {
      tippy(favoriteTooltip, {
        theme: 'light',
        content: `<div class="flex flex-col p-2 items-center justify-center">
            <span class="inline-block align-middle font-light">Add to Favorites</span>
            </div>`,
        allowHTML: true,
        placement: 'right'
      });
    }
    if (unFavoriteTooltip) {
      tippy(unFavoriteTooltip, {
        theme: 'light',
        content: `<div class="flex flex-col p-2 items-center justify-center">
            <span class="inline-block align-middle font-light">Remove from Favorites</span>
            </div>`,
        allowHTML: true,
        placement: 'right'
      });
    }
  }

  const getHContent = () => {
    if (fullHeight) return 'h-full';
    else if (fullSidebar) return 'h-content-no-banner';
    else return 'h-content';
  };

  const buttonClass =
    'flex items-center justify-center cursor-pointer transition-all duration-150 py-2 px-4 rounded border-2';
  const deleteTemplate = (item: PreviewImage) => {
    Swal.fire({
      showDenyButton: true,
      buttonsStyling: false,
      showCloseButton: true,
      title: 'Are you sure you want to delete this template?',
      denyButtonText: `No do not delete`,
      confirmButtonText: 'Yes PERMANENTLY Delete this template',
      text: `You are about to permanently delete this template`,
      customClass: {
        actions: 'flex gap-4',
        title: '!text-left border border-b border-gray-200',
        confirmButton: `${buttonClass} border-primary hover:bg-primary text-primary hover:text-white`,
        denyButton: `${buttonClass} border-green-500 bg-green-500 hover:bg-green-600 hover:border-green-600 text-white`
      },
      allowOutsideClick: () => !Swal.isLoading()
    }).then(async result => {
      if (result.isConfirmed) {
        const result = await new ThumbTemplatesService().deleteTemplate(item.id);
        if (result) {
          Swal.fire({
            position: 'bottom-end',
            icon: 'success',
            title: 'Template Deleted',
            showConfirmButton: false,
            timer: 1500,
            toast: true
          });
          thumbTemplates.getTemplates(page, limit, selectedShape, selectedTags, favorites, true, $thumbnail.type);
        }
        return true;
      }
      return false;
    });
  };

  const toggleFeature = async (item: PreviewImage, index: number) => {
    let featured = !item.isFeatured;
    const result = await new ThumbTemplatesService().featureTemplate(item.id, featured);

    if (result) {
      Swal.fire({
        position: 'bottom-end',
        icon: 'success',
        title: 'Template Updated',
        showConfirmButton: false,
        timer: 1500,
        toast: true
      });
      gridProps.images[index].isFeatured = featured;
    }
  };

  const toggleFavorite = async (item: PreviewImage, index: number) => {
    const result = await new ThumbTemplatesService().setFavorite(item.id);

    if (result) {
      Swal.fire({
        position: 'bottom-end',
        icon: 'success',
        title: 'Template Favorites Updated',
        showConfirmButton: false,
        timer: 1500,
        toast: true
      });
      gridProps.images[index].isFavorite = !item.isFavorite;
    }
  };
</script>

<div class="flex h-full flex-col bg-white">
  <div class="mx-6 flex flex-row">
    <p class="grow text-base font-bold text-black">Change Template</p>
    <details bind:open={isOpen}>
      <summary class={`flex h-8 w-8 cursor-pointer items-center justify-center rounded bg-gray-200 `}>
        <i class="fa-regular fa-sliders-simple text-gray-700"></i>
      </summary>
      <ul
        class="absolute right-6 z-30 mt-2 flex w-72 flex-col overflow-hidden rounded border border-gray-200 bg-white p-[10px] text-base font-semibold text-gray-600 shadow-xl disabled:bg-gray-400"
      >
        {#each shapes as shape}
          <button
            class={`flex h-11 cursor-pointer flex-row items-center   pl-4 hover:bg-gray-50 ${shape === selectedShape ? 'bg-gray-50' : 'bg-white'} `}
            on:click={() => setActiveFilter(shape)}
          >
            <Icon name={shape} className="w-8 h-8" />
            <span class="text-base font-semibold">{shape}</span>
          </button>
        {/each}
        <hr />
        <button
          class={`flex h-11 flex-row items-center pl-4 ${filterFavorites ? 'bg-gray-50' : 'bg-white'} cursor-pointer hover:bg-gray-50`}
          on:click={() => (filterFavorites = !filterFavorites)}
        >
          <Icon name="Star" className="w-8 px-1 h-8" />
          <span class={`text-base font-semibold `}>Favorites</span>
        </button>
      </ul>
    </details>
  </div>
  <!-- <div class="flex-grow" on:scroll={checkScrollEvent}> -->
  <div
    class="relative grid h-full auto-rows-max grid-cols-2 items-start gap-5 overflow-auto px-14 pt-6 {getHContent()}"
    on:scroll={checkScrollEvent}
  >
    {#each gridProps.images as item, i (i)}
      <div class="">
        <!--
        {#if isAdmin}
          <div
            class="w-full flex space-x-2 justify-center items-center"
            role="group"
          >
            <button
              on:click={() => deleteTemplate(item)}
              class="px-4 text-sm font-small py-1 border-2 rounded-md border-red-400 hover:bg-red-400 text-red-400 hover:text-white"
            >
              Delete Template
            </button>
            <button
              on:click={() => toggleFeature(item, i)}
              class="px-4 text-sm font-small py-1 border-2 rounded-md border-red-400 hover:bg-red-400 text-red-400 hover:text-white"
            >
              {#if item.isFeatured == false}
                Feature Template
              {/if}
              {#if item.isFeatured == true}
                Remove Feature
              {/if}
            </button>
          </div>
        {/if}
        -->
        <div class="relative aspect-auto w-full" data-template-id={item.id}>
          <button class="w-full" on:click={() => changeTemplate(item)}>
            <img
              class="aspect-auto w-full rounded-lg border border-slate-800 bg-gray-800 shadow-md"
              src={item.src}
              alt={item.title}
            />
          </button>
          <button
            on:click={() => toggleFavorite(item, i)}
            class="absolute right-1 top-1 z-20 rounded-full border-0 p-2"
          >
            {#if item.isFavorite == false}
              <span bind:this={favoriteTooltip}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="#FABE24"
                  class="bi bi-star-fill text-gray-500 hover:text-red-400"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z"
                  />
                </svg>
              </span>
            {/if}
            {#if item.isFavorite == true}
              <span bind:this={unFavoriteTooltip}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="#FABE24"
                  class="bi bi-star-fill text-[#FABE24]"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z"
                  />
                </svg>
              </span>
            {/if}
          </button>
        </div>
      </div>
    {/each}
  </div>
  <!-- </div> -->
</div>

<style lang="postcss">
  .h-content-no-banner {
    height: calc(100vh - 60px);
    height: calc(100svh - 60px);
  }
  .h-content {
    height: calc(100vh - (60px + 72px));
    height: calc(100svh - (60px + 72px));
  }
  ::-webkit-scrollbar {
    @apply w-2 rounded-full bg-slate-100 p-1;
  }
  ::-webkit-scrollbar-track {
    @apply rounded-full shadow-gray-500;
  }
  ::-webkit-scrollbar-thumb {
    @apply rounded-full bg-gray-400;
  }
  ::-webkit-scrollbar-thumb:hover {
    @apply rounded-full bg-gray-500;
  }
  details > summary {
    list-style-type: none;
    outline: none;
    cursor: pointer;
  }

  details > summary::-webkit-details-marker {
    display: none;
  }

  details > summary::before {
    content: '';
  }

  details[open] > summary::before {
    content: '';
  }
</style>
